import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { getCharacter } from '../utils/characters';

/** Delay between tap and outcome card appearing. */
const OUTCOME_DELAY_MS = 400;
/** Tier 1 only: how long to hold the outcome before auto-advancing. */
const TIER1_HOLD_MS = 2500;
/** Tier 2/3: minimum time outcome must be visible before Continue unlocks. */
const CONTINUE_LOCK_MS = 1500;

/**
 * Visual config per outcome type.
 * "learning" is NOT a failure — shown with warm amber, never red/wrong styling.
 */
const OUTCOME_THEME = {
  positive: {
    border: '2px solid rgba(34, 197, 94, 0.4)',
    background: 'rgba(34, 197, 94, 0.08)',
    accentColor: 'rgb(22, 163, 74)',
    label: 'Wonderful!',
  },
  learning: {
    border: '2px solid rgba(251, 191, 36, 0.4)',
    background: 'rgba(251, 191, 36, 0.07)',
    accentColor: 'rgb(161, 98, 7)',
  },
};

/**
 * Outcome image — resolves bare filenames to /images/story/ and silently hides
 * when the asset is missing (no new assets needed for testing).
 */
function OutcomeImage({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  const fullSrc = src.startsWith('/') ? src : `/images/story/${src}`;
  return (
    <img
      src={fullSrc}
      alt={alt || ''}
      onError={() => setFailed(true)}
      style={{
        display: 'block',
        marginTop: 12,
        maxWidth: '100%',
        maxHeight: 160,
        borderRadius: 10,
        objectFit: 'cover',
      }}
    />
  );
}

/**
 * StoryChoiceEngine — the child taps one of 2–3 choices and sees the outcome.
 *
 * When embedded in the guided lesson player, the player passes:
 *   { content, activityId, onComplete, guidedContext, lessonUiMode, ... }
 *
 * When used standalone (e.g. Kids Place WebView):
 *   { config, tier, onComplete, onSuccess, onFailure, onHint }
 *
 * `config` and `content` are aliases — whichever is present is used.
 *
 * Config / content shape:
 *   { situation: string, character?: string, choices: Choice[] }
 *
 * Choice shape:
 *   { id, text, outcome: 'positive'|'learning', outcomeText, outcomeImage? }
 *
 * Tier affects text size, tap-target height, and advance behaviour:
 *   Tier 1 (ages 4–6): auto-advances after TIER1_HOLD_MS, with a progress bar.
 *   Tier 2/3          : Continue button appears on tap, unlocks after CONTINUE_LOCK_MS.
 * Defaults to 1 when absent.
 */
export default function StoryChoiceEngine({
  // Lesson player props (content = activityData.content)
  content,
  activityId = 'story_choice',
  onComplete,
  guidedContext,
  // Standalone / story-player props
  config,
  tier = 1,
  onSuccess,
  onFailure,
  // onHint reserved for future audio / character hint integration
  onHint, // eslint-disable-line no-unused-vars
}) {
  const resolved = config ?? content ?? {};
  const { situation = '', choices = [], character = null, prompt = '' } = resolved;
  const singleSurface = guidedContext?.singleSurface === true;

  // Validate character name against the known protagonist roster
  if (character && !getCharacter(character)) {
    console.warn(
      `[StoryChoiceEngine] Unknown character "${character}" in activityId "${activityId}".` +
      ' Check docs/kiddoware_ai_story_protagonists.json.',
    );
  }

  const [selectedId, setSelectedId]         = useState(null);
  const [outcomeVisible, setOutcomeVisible] = useState(false);
  // tier 2/3: becomes true CONTINUE_LOCK_MS after the choice is tapped
  const [continueEnabled, setContinueEnabled] = useState(false);

  const tapTimerRef      = useRef(null);
  const holdTimerRef     = useRef(null);
  const continueTimerRef = useRef(null);

  const selectedChoice = choices.find((c) => c.id === selectedId) ?? null;
  const outcomeTheme   = OUTCOME_THEME[selectedChoice?.outcome] ?? OUTCOME_THEME.learning;

  // Tier-driven sizes — Tier 1 (ages 4–6) gets the largest targets and text
  const minBtnHeight  = tier === 1 ? 72 : tier === 2 ? 64 : 56;
  const situationSize = tier === 1 ? 18 : 16;
  const choiceSize    = tier === 1 ? 17 : 15;
  const outcomeSize   = tier === 1 ? 16 : 15;

  const handleTap = (choice) => {
    if (selectedId) return; // already chosen — locking is intentional
    setSelectedId(choice.id);
    // 400 ms pause so the selection highlight registers before outcome slides in
    tapTimerRef.current = setTimeout(() => {
      setOutcomeVisible(true);
      if (choice.outcome === 'positive') {
        onSuccess?.({ activityId, choiceId: choice.id });
      } else {
        onFailure?.({ activityId, choiceId: choice.id });
      }
    }, OUTCOME_DELAY_MS);
  };

  // Tier 1 only: auto-advance after TIER1_HOLD_MS once outcome is visible
  useEffect(() => {
    if (!outcomeVisible || tier !== 1) return undefined;
    holdTimerRef.current = setTimeout(() => {
      onComplete?.({ activityId, choiceId: selectedId });
    }, TIER1_HOLD_MS);
    return () => clearTimeout(holdTimerRef.current);
  }, [outcomeVisible, tier, activityId, selectedId, onComplete]);

  // Tier 2/3: start CONTINUE_LOCK_MS countdown from the moment of tap
  useEffect(() => {
    if (!selectedId || tier === 1) return undefined;
    continueTimerRef.current = setTimeout(() => {
      setContinueEnabled(true);
    }, CONTINUE_LOCK_MS);
    return () => clearTimeout(continueTimerRef.current);
  }, [selectedId, tier]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(tapTimerRef.current);
      clearTimeout(holdTimerRef.current);
      clearTimeout(continueTimerRef.current);
    };
  }, []);

  // Shell: flat/transparent inside a lesson card; card surface when standalone
  const shell = {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: 8,
    justifyContent: 'flex-start',
    ...(singleSurface
      ? { maxWidth: '100%', padding: '4px 0 12px' }
      : {
          maxWidth: 480,
          margin: '0 auto',
          padding: '16px 14px 20px',
          borderRadius: 20,
          background: 'var(--color-bg-card)',
          border: '2px solid var(--color-border)',
          boxShadow: '0 6px 24px var(--color-shadow)',
        }),
  };

  if (choices.length === 0) {
    return (
      <div style={shell}>
        <p style={{ color: 'var(--color-text)', opacity: 0.6, fontSize: 14, margin: 0 }}>
          No choices configured for this activity.
        </p>
      </div>
    );
  }

  return (
    <div style={shell}>
      <style>{`
        @keyframes storyOutcomeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes storyProgressIn {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes storyContinuePulse {
          0%   { box-shadow: 0 0 0 0    rgba(139, 92, 246, 0.55); }
          60%  { box-shadow: 0 0 0 10px rgba(139, 92, 246, 0);   }
          100% { box-shadow: 0 0 0 0    rgba(139, 92, 246, 0);   }
        }
      `}</style>

      {/* Bold question header */}
      {prompt ? (
        <p
          style={{
            margin: '0 0 2px',
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--color-text)',
            lineHeight: 1.4,
          }}
        >
          {prompt}
        </p>
      ) : null}

      {/* Situation — left accent border, no grey box */}
      {situation ? (
        <p
          style={{
            margin: '0 0 4px',
            fontSize: situationSize,
            fontWeight: 400,
            color: 'var(--color-text-secondary, var(--color-text))',
            lineHeight: 1.45,
            borderLeft: '3px solid var(--color-primary)',
            paddingLeft: 12,
          }}
        >
          {situation}
        </p>
      ) : null}

      {/* Choice buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16, marginBottom: 0 }} role="group" aria-label="Choices">
        {choices.map((choice) => {
          const isSelected = choice.id === selectedId;
          const isDimmed   = !!selectedId && !isSelected;
          return (
            <button
              key={choice.id}
              type="button"
              disabled={!!selectedId}
              onClick={() => handleTap(choice)}
              aria-pressed={isSelected}
              style={{
                minHeight: 52,
                padding: '12px 16px',
                borderRadius: 14,
                // Selected → immediate purple accent (neutral, not outcome-colour)
                border: isSelected
                  ? '2px solid var(--color-primary)'
                  : '2px solid var(--color-border)',
                background: isSelected
                  ? 'rgba(139, 92, 246, 0.12)'
                  : 'var(--color-bg-card)',
                color: 'var(--color-text)',
                fontSize: choiceSize,
                fontWeight: isSelected ? 700 : 600,
                cursor: selectedId ? 'default' : 'pointer',
                opacity: isDimmed ? 0.5 : 1,
                width: '100%',
                boxSizing: 'border-box',
                touchAction: 'manipulation',
                transition: 'border-color 0.2s ease, background 0.2s ease, opacity 0.2s ease, transform 0.15s ease',
                transform: isSelected ? 'scale(1.015)' : 'scale(1)',
              }}
            >
              {/* Flex row: text left, checkmark right on selection */}
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1, textAlign: 'left', lineHeight: 1.35 }}>{choice.text}</span>
                {isSelected && (
                  <Check
                    size={tier === 1 ? 22 : 18}
                    strokeWidth={2.5}
                    aria-hidden
                    style={{ flexShrink: 0, color: 'var(--color-primary)' }}
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Outcome card — fades in after OUTCOME_DELAY_MS */}
      {outcomeVisible && selectedChoice ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginTop: 4,
            padding: '12px 14px',
            borderRadius: 14,
            border: outcomeTheme.border,
            background: outcomeTheme.background,
            animation: 'storyOutcomeIn 0.3s ease forwards',
          }}
        >
          {outcomeTheme.label ? (
            <p
              style={{
                margin: '0 0 5px',
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: outcomeTheme.accentColor,
              }}
            >
              {outcomeTheme.label}
            </p>
          ) : null}
          <p
            style={{
              margin: 0,
              fontSize: outcomeSize,
              fontWeight: 600,
              color: 'var(--color-text)',
              lineHeight: 1.5,
            }}
          >
            {selectedChoice.outcomeText}
          </p>
          {selectedChoice.outcomeImage ? (
            <OutcomeImage src={selectedChoice.outcomeImage} alt={selectedChoice.text} />
          ) : null}
        </div>
      ) : null}

      {/* Tier 1: thin progress bar fills over TIER1_HOLD_MS so child sees progress */}
      {tier === 1 && outcomeVisible ? (
        <div
          aria-hidden
          style={{
            height: 3,
            background: 'var(--color-border)',
            borderRadius: 999,
            overflow: 'hidden',
            marginTop: 2,
          }}
        >
          <div
            style={{
              height: '100%',
              background: 'var(--color-primary)',
              borderRadius: 999,
              animation: `storyProgressIn ${TIER1_HOLD_MS}ms linear forwards`,
            }}
          />
        </div>
      ) : null}

      {/* Tier 2/3: Continue button — visible immediately on tap, locked for CONTINUE_LOCK_MS */}
      {tier !== 1 && selectedId ? (
        <button
          type="button"
          disabled={!continueEnabled}
          onClick={() => onComplete?.({ activityId, choiceId: selectedId })}
          aria-label={continueEnabled ? 'Continue to the next step' : 'Please read the outcome first'}
          style={{
            marginTop: 4,
            width: '100%',
            minHeight: 52,
            borderRadius: 14,
            border: continueEnabled ? 'none' : '1px solid var(--color-border)',
            background: continueEnabled ? 'var(--color-primary)' : 'var(--color-bg-card)',
            color: continueEnabled ? '#fff' : 'var(--color-text)',
            fontWeight: 800,
            fontSize: 15,
            cursor: continueEnabled ? 'pointer' : 'default',
            opacity: continueEnabled ? 1 : 0.45,
            // Pulse fires once continueEnabled flips to true (animation: none → named triggers restart)
            animation: continueEnabled ? 'storyContinuePulse 0.9s ease-out 2' : 'none',
            transition: 'background 0.25s ease, color 0.25s ease, opacity 0.25s ease, border 0.25s ease',
          }}
        >
          Continue
        </button>
      ) : null}
    </div>
  );
}
